// @purpose Canonical localStorage-backed user preferences: dashboard view modes, sidebar collapse state, filter visibility, section-card collapse, security time-range, AG Grid column state and page-size persistence. Use these helpers instead of touching localStorage directly.
export const APP_PREFS_STORAGE_KEY = 'app_prefs'

// Dashboard-specific preference keys
export const DASHBOARD_PREFS_KEYS = {
  INCIDENTS: 'incidents_view_prefs',
  // AI Incidents (/ai-applications/incidents) is a separate surface from the
  // older INCIDENTS dashboard above and keeps its own Top 9 / Grid choice
  // (PENG-13907). Do not merge the two keys — they are different pages.
  AI_INCIDENTS: 'ai_incidents_view_prefs',
  EVENTS: 'events_view_prefs',
  AUDIT: 'audit_view_prefs',
  INFRASTRUCTURE: 'infrastructure_view_prefs',
  NETWORK: 'network_view_prefs',
  PLATFORM: 'platform_view_prefs',
  DATA: 'data_view_prefs',
  COMPUTE: 'compute_view_prefs',
  APPLICATION: 'application_view_prefs',
  // Application drawer tab preferences (one key per resource type)
  APP_DRAWER_SERVICE: 'app_drawer_service_tab',
  APP_DRAWER_SITE: 'app_drawer_site_tab',
  APP_DRAWER_ENDPOINT: 'app_drawer_endpoint_tab',
  APP_DRAWER_IMAGE: 'app_drawer_image_tab',
  MISCONFIGURATIONS: 'misconfigurations_view_prefs',
  VULNERABILITIES: 'vulnerabilities_view_prefs',
  SENSITIVE_DATA: 'sensitive_data_view_prefs',
  GLOBAL: 'global_view_prefs',
  SECURITY: 'security_view_prefs', // Shared across all security dashboards
} as const

// Sidebar section preference keys
export const SIDEBAR_COLLAPSE_KEY = 'sidebar_section_collapse_prefs'

// Sidebar main collapse preference key
export const SIDEBAR_MAIN_COLLAPSE_KEY = 'sidebar_main_collapse_prefs'

// Filter visibility preference key
export const FILTER_VISIBILITY_PREFS_KEY = 'filter_visibility_prefs'

// Overview dashboard preference keys
export const OVERVIEW_DASHBOARD_PREFS_KEY = 'overview_dashboard_prefs'

// Section card collapse preference key
export const SECTION_CARD_PREFS_KEY = 'section_card_prefs'

export type DashboardViewPrefs = {
  viewMode: 'card' | 'grid'
  timeRange?: string // Time range preference for security dashboards
  activeTab?: string // Active tab preference for drawers/detail panels
  attackPathViewMode?: 'timeline' | 'grouped' // Attack path visualization mode
}

export type FilterVisibilityConfig = {
  visibleFilterIds: string[] // IDs of filters currently visible beyond the first 5
  defaultFiltersCount: number // Number of default filters to show (default: 5)
}

export type FilterVisibilityPrefs = {
  [dashboardKey: string]: FilterVisibilityConfig
}

export type SidebarCollapsePrefs = {
  [sectionTitle: string]: boolean
}

export type SectionCardCollapsePrefs = {
  [sectionId: string]: boolean
}

export type AppPrefs = {
  cardOrGridView?: 'card' | 'grid'
  filterPanelExpanded?: boolean // App-wide filter panel state
  summaryBlurbsExpanded?: boolean // App-wide summary-metric strip state
}

export type OverviewDashboardPrefs = {
  dashboardView: 'executive' | 'analyst' // Toggle between executive/analyst view
  compactView: boolean // Compact vs full view when in analyst mode
}

export const canSetAppPrefs = () => {
  try {
    const storage = window.localStorage
    const x = '__storage_test__'
    storage.setItem(x, x)
    storage.removeItem(x)
    return true
  } catch {
    return false
  }
}

export const setAppPrefs = <T>(vuln_prefs: T, storage_key?: string) => {
  localStorage.setItem(
    storage_key || APP_PREFS_STORAGE_KEY,
    JSON.stringify(vuln_prefs),
  )
}

export const getAppPrefs = <T>(storage_key?: string): T | undefined => {
  // The getItem call is INSIDE the try: reading localStorage throws outright
  // when storage is unavailable (Safari private mode, a sandboxed iframe, or
  // a browser with cookies/site-data blocked) rather than returning null.
  // These readers run inside useState initializers — see useDashboardPage's
  // initialViewMode — so an escaping SecurityError takes the whole dashboard
  // down rather than costing the user a remembered preference. Returning
  // undefined lets each caller fall back to its own configured default.
  try {
    const savedState = localStorage.getItem(
      storage_key || APP_PREFS_STORAGE_KEY,
    )
    if (!savedState) {
      return undefined
    }
    return JSON.parse(savedState)
  } catch (e) {
    console.error('Error reading saved prefs: ' + storage_key, e)
    return undefined
  }
}

// Dashboard-specific helper functions

/**
 * Persist a dashboard's view mode under its own key.
 *
 * Returns whether the write actually landed, so a caller that promised the
 * user their choice would be remembered can say otherwise when it wasn't.
 * `canSetAppPrefs()` is a probe, not a guarantee — the real `setItem` can
 * still fail after it passes (quota exhausted, storage revoked between the
 * two calls), so the write itself is guarded rather than trusted.
 */
export const setDashboardViewPrefs = (
  dashboardKey: keyof typeof DASHBOARD_PREFS_KEYS,
  viewMode: 'card' | 'grid',
): boolean => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save dashboard view preferences - localStorage not available',
    )
    return false
  }

  const prefs: DashboardViewPrefs = { viewMode }
  try {
    setAppPrefs(prefs, DASHBOARD_PREFS_KEYS[dashboardKey])
    return true
  } catch (e) {
    console.error('Error saving dashboard view preferences', e)
    return false
  }
}

export const getDashboardViewPrefs = (
  dashboardKey: keyof typeof DASHBOARD_PREFS_KEYS,
): DashboardViewPrefs | undefined => {
  return getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS[dashboardKey])
}

export const getDashboardViewMode = (
  dashboardKey: keyof typeof DASHBOARD_PREFS_KEYS,
  defaultViewMode: 'card' | 'grid' = 'grid',
): 'card' | 'grid' => {
  const prefs = getDashboardViewPrefs(dashboardKey)
  return prefs?.viewMode || defaultViewMode
}

// Sidebar collapse helper functions
export const setSidebarSectionCollapse = (
  sectionTitle: string,
  isCollapsed: boolean,
) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save sidebar collapse preferences - localStorage not available',
    )
    return
  }

  const existingPrefs =
    getAppPrefs<SidebarCollapsePrefs>(SIDEBAR_COLLAPSE_KEY) || {}
  const updatedPrefs: SidebarCollapsePrefs = {
    ...existingPrefs,
    [sectionTitle]: isCollapsed,
  }
  setAppPrefs(updatedPrefs, SIDEBAR_COLLAPSE_KEY)
}

export const getSidebarSectionCollapse = (sectionTitle: string): boolean => {
  const prefs = getAppPrefs<SidebarCollapsePrefs>(SIDEBAR_COLLAPSE_KEY)
  if (!prefs) {
    // Default: Administration is collapsed, all others expanded
    return sectionTitle === 'Administration'
  }
  return prefs[sectionTitle] ?? sectionTitle === 'Administration'
}

// Main sidebar collapse helper functions
export const setSidebarMainCollapse = (isCollapsed: boolean) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save sidebar main collapse preference - localStorage not available',
    )
    return
  }

  setAppPrefs(isCollapsed, SIDEBAR_MAIN_COLLAPSE_KEY)
}

export const getSidebarMainCollapse = (): boolean => {
  const isCollapsed = getAppPrefs<boolean>(SIDEBAR_MAIN_COLLAPSE_KEY)
  return isCollapsed ?? false // Default to expanded
}

// Global view preference helper functions
export const setGlobalViewPrefs = (viewMode: 'card' | 'grid') => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save global view preferences - localStorage not available',
    )
    return
  }

  const prefs: DashboardViewPrefs = { viewMode }
  setAppPrefs(prefs, DASHBOARD_PREFS_KEYS.GLOBAL)
}

export const getGlobalViewPrefs = (): DashboardViewPrefs | undefined => {
  return getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS.GLOBAL)
}

export const getGlobalViewMode = (
  defaultViewMode: 'card' | 'grid' = 'grid',
): 'card' | 'grid' => {
  const prefs = getGlobalViewPrefs()
  return prefs?.viewMode || defaultViewMode
}

// Security dashboard time range preference helper functions
export const setSecurityTimeRangePrefs = (timeRange: string) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save security time range preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.SECURITY,
  ) || { viewMode: 'grid' }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    timeRange,
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.SECURITY)
}

export const getSecurityTimeRangePrefs = (): string => {
  const prefs = getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS.SECURITY)
  return prefs?.timeRange || 'all' // Default to 'all' time range
}

// Enhanced dashboard preference setters to handle time range
export const setSecurityDashboardPrefs = (
  viewMode: 'card' | 'grid',
  timeRange?: string,
) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save security dashboard preferences - localStorage not available',
    )
    return
  }

  const existingPrefs =
    getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS.SECURITY) || {}
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    viewMode,
    ...(timeRange && { timeRange }),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.SECURITY)
}

// Security Events tab preference helper functions
export const setSecurityEventsActiveTab = (tabIndex: number) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save security events tab preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.EVENTS,
  ) || { viewMode: 'grid' }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    activeTab: tabIndex.toString(),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.EVENTS)
}

export const getSecurityEventsActiveTab = (): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS.EVENTS)
  const tabIndex = prefs?.activeTab ? parseInt(prefs.activeTab, 10) : 0
  // Ensure valid tab index (0-5 for the 6 tabs)
  return isNaN(tabIndex) || tabIndex < 0 || tabIndex > 5 ? 0 : tabIndex
}

// Security Incidents tab preference helper functions
export const setSecurityIncidentsActiveTab = (tabIndex: number) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save security incidents tab preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.INCIDENTS,
  ) || { viewMode: 'grid' }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    activeTab: tabIndex.toString(),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.INCIDENTS)
}

export const getSecurityIncidentsActiveTab = (): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS.INCIDENTS)
  const tabIndex = prefs?.activeTab ? parseInt(prefs.activeTab, 10) : 0
  // Ensure valid tab index (0-5 for the 6 tabs: Overview, Attack Path, Response, Blast Radius, Function Calls, Timeline)
  return isNaN(tabIndex) || tabIndex < 0 || tabIndex > 5 ? 0 : tabIndex
}

// Risk dashboards tab preference helper functions

// Misconfigurations
export const setMisconfigurationsActiveTab = (tabIndex: number) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save misconfigurations tab preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.MISCONFIGURATIONS,
  ) || { viewMode: 'grid' }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    activeTab: tabIndex.toString(),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.MISCONFIGURATIONS)
}

export const getMisconfigurationsActiveTab = (): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.MISCONFIGURATIONS,
  )
  const tabIndex = prefs?.activeTab ? parseInt(prefs.activeTab, 10) : 0
  // Tabs: Overview, Technical Details, Response (conditional), Blast Radius, Timeline, Tickets
  // Max 6 tabs (when Response is shown), valid indices are 0-5, default to 0 (Overview)
  return isNaN(tabIndex) || tabIndex < 0 || tabIndex >= 6 ? 0 : tabIndex
}

// Vulnerabilities
export const setVulnerabilitiesActiveTab = (tabIndex: number) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save vulnerabilities tab preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.VULNERABILITIES,
  ) || { viewMode: 'grid' }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    activeTab: tabIndex.toString(),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.VULNERABILITIES)
}

export const getVulnerabilitiesActiveTab = (): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.VULNERABILITIES,
  )
  const tabIndex = prefs?.activeTab ? parseInt(prefs.activeTab, 10) : 0
  // Tabs: Overview, Technical Details, Runtime Context (conditional), Response (conditional), Blast Radius, Timeline, Tickets
  // Max 7 tabs (when Runtime Context and Response are shown), valid indices are 0-6, default to 0 (Overview)
  return isNaN(tabIndex) || tabIndex < 0 || tabIndex >= 7 ? 0 : tabIndex
}

// Sensitive Data
export const setSensitiveDataActiveTab = (tabIndex: number) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save sensitive data tab preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.SENSITIVE_DATA,
  ) || {
    viewMode: 'grid',
  }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    activeTab: tabIndex.toString(),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.SENSITIVE_DATA)
}

export const getSensitiveDataActiveTab = (): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.SENSITIVE_DATA,
  )
  const tabIndex = prefs?.activeTab ? parseInt(prefs.activeTab, 10) : 0
  // Tabs: Overview, Technical Details, Response (conditional), Blast Radius, Timeline, Tickets
  // Max 6 tabs (when Response is shown), valid indices are 0-5, default to 0 (Overview)
  return isNaN(tabIndex) || tabIndex < 0 || tabIndex >= 6 ? 0 : tabIndex
}

// Infrastructure dashboards tab preference helper functions

// Compute
export const setComputeActiveTab = (tabIndex: number) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save compute tab preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.COMPUTE,
  ) || {
    viewMode: 'grid',
  }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    activeTab: tabIndex.toString(),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.COMPUTE)
}

export const getComputeActiveTab = (): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS.COMPUTE)
  const tabIndex = prefs?.activeTab ? parseInt(prefs.activeTab, 10) : 0
  // Flexible max index - infrastructure tabs can vary
  return isNaN(tabIndex) || tabIndex < 0 ? 0 : tabIndex
}

// Network
export const setNetworkActiveTab = (tabIndex: number) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save network tab preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.NETWORK,
  ) || {
    viewMode: 'grid',
  }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    activeTab: tabIndex.toString(),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.NETWORK)
}

export const getNetworkActiveTab = (): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS.NETWORK)
  const tabIndex = prefs?.activeTab ? parseInt(prefs.activeTab, 10) : 0
  return isNaN(tabIndex) || tabIndex < 0 ? 0 : tabIndex
}

// Data
export const setDataActiveTab = (tabIndex: number) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save data tab preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.DATA,
  ) || {
    viewMode: 'grid',
  }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    activeTab: tabIndex.toString(),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.DATA)
}

export const getDataActiveTab = (): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS.DATA)
  const tabIndex = prefs?.activeTab ? parseInt(prefs.activeTab, 10) : 0
  return isNaN(tabIndex) || tabIndex < 0 ? 0 : tabIndex
}

// Platform
export const setPlatformActiveTab = (tabIndex: number) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save platform tab preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.PLATFORM,
  ) || {
    viewMode: 'grid',
  }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    activeTab: tabIndex.toString(),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.PLATFORM)
}

export const getPlatformActiveTab = (): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS.PLATFORM)
  const tabIndex = prefs?.activeTab ? parseInt(prefs.activeTab, 10) : 0
  return isNaN(tabIndex) || tabIndex < 0 ? 0 : tabIndex
}

// Application
export const setApplicationActiveTab = (tabIndex: number) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save application tab preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.APPLICATION,
  ) || {
    viewMode: 'grid',
  }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    activeTab: tabIndex.toString(),
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.APPLICATION)
}

export const getApplicationActiveTab = (): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.APPLICATION,
  )
  const tabIndex = prefs?.activeTab ? parseInt(prefs.activeTab, 10) : 0
  return isNaN(tabIndex) || tabIndex < 0 ? 0 : tabIndex
}

// ============================================================================
// AG GRID COLUMN PREFERENCES
// ============================================================================
// Exact same logic as import/save-prefs.ts for column preferences

import type {
  ColDef,
  ColGroupDef,
  ColumnState,
  GridApi,
} from 'ag-grid-community'

export const getColumnPrefsLocalStorageKey = (id?: string): string => {
  return `datagrid_col_prefs_${id || 'generic_table'}`
}

export const hasCustomWidth = (obj: ColumnState | ColDef): boolean => {
  return !obj.flex
}

export const setHasCustomWidth = (obj: ColumnState | ColDef): void => {
  obj.flex = 0
}

// Loads an array of ColumnState configurations from LocalStorage
// for the specified grid (keyed off of the id).
export const loadColumnState = (id?: string): ColumnState[] | undefined => {
  const storageKey = getColumnPrefsLocalStorageKey(id)
  const savedState = localStorage.getItem(storageKey)
  if (!savedState) {
    return undefined
  }
  try {
    return JSON.parse(savedState)
  } catch (e) {
    console.error(`Error parsing saved column prefs for ${storageKey}`, e)
    return undefined
  }
}

// Saves an array of ColumnState configurations to LocalStorage
// for the specified grid (keyed off of the id).
export const saveColumnState = (
  api: GridApi,
  id?: string,
  // column IDs for columns that just had custom widths set
  modifiedWidthColumnIDs?: string[],
) => {
  if (api && !api.isDestroyed()) {
    const currentState: ColumnState[] = api.getColumnState()
    const savedState = currentState.map((colState: ColumnState) => {
      const colDef = api.getColumnDef(colState.colId)
      // If this column was just set with a manual/custom width, flag this column.
      if (colDef && modifiedWidthColumnIDs?.includes(colState.colId)) {
        setHasCustomWidth(colDef)
      }
      const simplifiedColState: ColumnState = {
        colId: colState.colId,
        pinned: colState.pinned,
        sort: colState.sort,
        sortIndex: colState.sortIndex,
        hide: colState.hide,
      }
      if (colDef && hasCustomWidth(colDef)) {
        simplifiedColState.width = colState.width
      }
      return simplifiedColState
    })
    localStorage.setItem(
      getColumnPrefsLocalStorageKey(id),
      JSON.stringify(savedState),
    )
  }
}

export const removeColumnState = (id?: string) => {
  localStorage.removeItem(getColumnPrefsLocalStorageKey(id))
}

// The `flex`/`width` a page's column def declared BEFORE any saved pref was
// applied to it. `augmentColumnPrefs` mutates the caller's column def objects
// in place (they're memoized per page, so the mutations outlive any single
// render), which means a reset has to be able to put the declared values back.
// Keyed off the def object itself rather than stashed on the def: `initialFlex`
// / `initialWidth` are real AG-Grid props with their own meaning, and
// `initialFlex` in particular can't represent "this column declared no flex"
// (undefined) distinctly from "we never stashed anything" (also undefined) —
// which is exactly how a user-set width used to survive Reset to Column
// Defaults on grids whose columns don't declare flex.
const declaredSizing = new WeakMap<
  ColDef,
  { flex?: number | null; width?: number }
>()

const rememberDeclaredSizing = (colDef: ColDef): void => {
  if (!declaredSizing.has(colDef)) {
    declaredSizing.set(colDef, { flex: colDef.flex, width: colDef.width })
  }
}

// Resets the localstorage saved prefs to be just the original column orders and flex values.
export const resetColumnState = (
  colDefs: (ColDef | ColGroupDef)[] | null | undefined,
  id?: string,
) => {
  colDefs?.forEach((colDef: ColDef) => {
    const declared = declaredSizing.get(colDef)
    if (declared) {
      // Put back exactly what the page declared, including clearing a width
      // (and the flex: 0 that came with it) that a saved pref baked in.
      colDef.flex = declared.flex
      colDef.width = declared.width
    } else if (colDef.initialFlex) {
      colDef.flex = colDef.initialFlex
    }
    // unset any pinning
    colDef.pinned = false
    // reset to initial hide state
    if (colDef.initialPivotIndex) {
      colDef.hide = colDef.initialPivotIndex === 1
    }
  })
  removeColumnState(id)
}

// With the provided saved ColumnStates, update the grid columns with a
// subset of the saved params.
export const restoreColumnPrefs = (api: GridApi, savedState: ColumnState[]) => {
  // NOTE: The following is the built-in mechanism from ag-grid for applying
  // state, however it does not work appropriately for our needs
  // (want fine-grained control over the logic behind flex/width)
  //
  // if (savedState) {
  //   api.applyColumnState({
  //     state: savedState,
  //     applyOrder: true,
  //   })
  // }
  if (savedState) {
    api.setGridOption(
      'columnDefs',
      augmentColumnPrefs(api.getColumnDefs(), savedState),
    )
  }
}

export const augmentColumnPrefs = (
  colDefs: (ColDef | ColGroupDef)[] | null | undefined,
  savedState: ColumnState[],
) => {
  // Return columns sorted by the column order of the columns
  // in the saved prefs.
  const originalColDefs = colDefs || []

  const processedColumns = originalColDefs
    .map((colDef: ColDef, originalIndex: number) => {
      const COL_ID = colDef.colId || colDef.field
      // Find the index of the matching colId
      const index = savedState.findIndex(
        (savedPref: ColumnState) => COL_ID === savedPref.colId,
      )
      // Find the last (non-pinned) column
      const lastCol = savedState
        .slice()
        .reverse()
        .find((savedPref: ColumnState) => !savedPref.pinned)
      // Set {pinned, width, hide} configurations - we do this
      // on a per-attribute basis for now (as opposed to a block _.assign)
      // as there are a lot of prefs saved and this will allow for fine-
      // grained control over what preferences are restored.
      // Stash the declared flex/width before this pass can mutate either, so
      // Reset to Column Defaults has something faithful to restore. Done for
      // EVERY def (not just ones present in savedState) — a column that isn't
      // in the saved state yet still gets reset alongside its neighbours.
      rememberDeclaredSizing(colDef)
      if (index > -1) {
        const foundPref = savedState[index]
        // Legacy stash, kept because `initialFlex` is also read on the reset
        // path for defs that predate `declaredSizing`.
        colDef.initialFlex ||= colDef.flex ?? undefined
        // Stash any original hide value for resetting in the initialPivotIndex value.
        colDef.initialPivotIndex ||= colDef.hide ? 1 : 2
        colDef.pinned = foundPref.pinned
        colDef.sort = foundPref.sort
        // If this column width came from the user, update the column def with
        // the width and set the flex value to 0.  Also set the custom width flag
        // on the column def.
        if (foundPref.width && hasCustomWidth(foundPref)) {
          setHasCustomWidth(colDef)
          colDef.width = foundPref.width
          // If is the last column (and no width is saved yet),
          // set flex to fill out the remainder of the grid.
          if (!foundPref.width && COL_ID === lastCol?.colId) {
            colDef.flex = foundPref.flex || 1
          }
        }
        colDef.hide = foundPref.hide || false
      }
      // Return index (for sorting algo) and originalIndex for new columns
      return { colDef, index, originalIndex }
    })
    .sort((a, b) => a.index - b.index)

  // Second pass: Place new/unknown columns (index = -1) at their original positions
  // rather than at the beginning of the grid
  const knownColumns = processedColumns.filter(item => item.index !== -1)
  const newColumns = processedColumns.filter(item => item.index === -1)

  if (newColumns.length === 0) {
    // No new columns, return the sorted result
    return processedColumns.map(item => item.colDef)
  }

  // Insert new columns at their original positions relative to known columns
  const result: ColDef[] = []
  let knownIndex = 0

  for (
    let originalIndex = 0;
    originalIndex < originalColDefs.length;
    originalIndex++
  ) {
    // Check if there's a new column at this original position
    const newColumn = newColumns.find(
      item => item.originalIndex === originalIndex,
    )
    if (newColumn) {
      result.push(newColumn.colDef)
    } else {
      // Find the next known column in the sorted order
      if (knownIndex < knownColumns.length) {
        const knownColumn = knownColumns[knownIndex]
        result.push(knownColumn.colDef)
        knownIndex++
      }
    }
  }

  return result
}

// Filter visibility preference helper functions
export const setFilterVisibilityPrefs = (
  dashboardKey: string,
  config: FilterVisibilityConfig,
) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save filter visibility preferences - localStorage not available',
    )
    return
  }

  const existingPrefs =
    getAppPrefs<FilterVisibilityPrefs>(FILTER_VISIBILITY_PREFS_KEY) || {}
  const updatedPrefs = {
    ...existingPrefs,
    [dashboardKey]: config,
  }
  setAppPrefs(updatedPrefs, FILTER_VISIBILITY_PREFS_KEY)
}

export const getFilterVisibilityPrefs = (
  dashboardKey: string,
): FilterVisibilityConfig => {
  const prefs = getAppPrefs<FilterVisibilityPrefs>(FILTER_VISIBILITY_PREFS_KEY)
  return (
    prefs?.[dashboardKey] || {
      visibleFilterIds: [],
      defaultFiltersCount: 5,
    }
  )
}

export const addVisibleFilter = (dashboardKey: string, filterId: string) => {
  const currentPrefs = getFilterVisibilityPrefs(dashboardKey)
  if (!currentPrefs.visibleFilterIds.includes(filterId)) {
    setFilterVisibilityPrefs(dashboardKey, {
      ...currentPrefs,
      visibleFilterIds: [...currentPrefs.visibleFilterIds, filterId],
    })
  }
}

export const removeVisibleFilter = (dashboardKey: string, filterId: string) => {
  const currentPrefs = getFilterVisibilityPrefs(dashboardKey)
  setFilterVisibilityPrefs(dashboardKey, {
    ...currentPrefs,
    visibleFilterIds: currentPrefs.visibleFilterIds.filter(
      id => id !== filterId,
    ),
  })
}

// App-wide filter panel visibility state helper functions
export const setFilterPanelState = (isExpanded: boolean) => {
  if (!canSetAppPrefs()) {
    console.warn('Cannot save filter panel state - localStorage not available')
    return
  }

  const existingPrefs = getAppPrefs<AppPrefs>() || {}
  const updatedPrefs = {
    ...existingPrefs,
    filterPanelExpanded: isExpanded,
  }
  setAppPrefs(updatedPrefs)
}

export const getFilterPanelState = (): boolean => {
  const prefs = getAppPrefs<AppPrefs>()
  return prefs?.filterPanelExpanded ?? false // Default to collapsed
}

// App-wide summary-blurb strip state — same shape and storage as the filter
// panel above, so the two collapsible dashboard regions behave identically.
// Defaults to EXPANDED (unlike the filter panel): the metrics are the point of
// the strip, and collapsing is the deliberate act of someone short on height.
export const setSummaryBlurbsState = (isExpanded: boolean) => {
  if (!canSetAppPrefs()) {
    console.warn('Cannot save summary blurb state - localStorage not available')
    return
  }

  const existingPrefs = getAppPrefs<AppPrefs>() || {}
  const updatedPrefs = {
    ...existingPrefs,
    summaryBlurbsExpanded: isExpanded,
  }
  setAppPrefs(updatedPrefs)
}

export const getSummaryBlurbsState = (): boolean => {
  const prefs = getAppPrefs<AppPrefs>()
  return prefs?.summaryBlurbsExpanded ?? true // Default to expanded
}

// PENG-14095 — per-tenant "I have triaged this week's new discoveries"
// bookmark. Stores the newest `firstSeenAt` the user has already acknowledged;
// the Discoveries strip stays hidden until a batch with a NEWER first-seen
// timestamp shows up, at which point it returns on its own. Scoped per tenant
// so one tenant's dismiss can't suppress another tenant's new findings.
const DISCOVERIES_DISMISSED_KEY = 'discoveries_dismissed_until'

type DiscoveriesDismissedPrefs = {
  [tenantId: string]: string // ISO 8601 timestamp
}

export const setDiscoveriesDismissedUntil = (
  tenantId: string,
  isoTimestamp: string,
): void => {
  if (!canSetAppPrefs() || !tenantId) return
  const existing =
    getAppPrefs<DiscoveriesDismissedPrefs>(DISCOVERIES_DISMISSED_KEY) || {}
  // `canSetAppPrefs()` probes with a two-byte value, so it answers "storage
  // is reachable", not "this write will fit" — a quota-exceeded failure on
  // the real payload lands HERE, past the guard. `setAppPrefs` does a bare
  // `setItem`, so an uncaught throw would escape the strip's dismiss handler
  // before it records the in-memory dismissal, and the × would do nothing at
  // all. Losing the persistence is the acceptable half of that failure;
  // losing the dismiss is not.
  try {
    setAppPrefs<DiscoveriesDismissedPrefs>(
      { ...existing, [tenantId]: isoTimestamp },
      DISCOVERIES_DISMISSED_KEY,
    )
  } catch (e) {
    console.warn('Could not persist the discoveries dismissal', e)
  }
}

export const getDiscoveriesDismissedUntil = (
  tenantId: string,
): string | null => {
  if (!tenantId) return null
  const prefs = getAppPrefs<DiscoveriesDismissedPrefs>(
    DISCOVERIES_DISMISSED_KEY,
  )
  return prefs?.[tenantId] ?? null
}

// PENG-14135 — per-tenant "the Discoveries strip is expanded" state. Separate
// pref key from the dismiss above: dismissing hides the strip entirely until
// a newer batch arrives, while expanding opens the top-N preview inline — two
// different user intents. Stored per-tenant so one tenant's preview state can
// not bleed across to another. Default collapsed.
const DISCOVERIES_EXPANDED_KEY = 'discoveries_expanded'

type DiscoveriesExpandedPrefs = {
  [tenantId: string]: boolean
}

export const setDiscoveriesExpanded = (
  tenantId: string,
  expanded: boolean,
): void => {
  if (!canSetAppPrefs() || !tenantId) return
  const existing =
    getAppPrefs<DiscoveriesExpandedPrefs>(DISCOVERIES_EXPANDED_KEY) || {}
  // Guarded exactly like setDiscoveriesDismissedUntil above: setAppPrefs
  // calls localStorage.setItem bare, which throws on a quota error and
  // throws outright where storage is unavailable at all (Safari private
  // mode, a sandboxed iframe) — see getAppPrefs' own comment. The caller is
  // DiscoveriesStrip's toggleExpanded, which invokes this INSIDE a
  // setExpanded updater, so an escaping error would break the toggle
  // mid-render rather than merely costing a remembered preference.
  try {
    setAppPrefs<DiscoveriesExpandedPrefs>(
      { ...existing, [tenantId]: expanded },
      DISCOVERIES_EXPANDED_KEY,
    )
  } catch (e) {
    console.warn('Could not persist the discoveries expansion state', e)
  }
}

export const getDiscoveriesExpanded = (tenantId: string): boolean => {
  if (!tenantId) return false
  const prefs = getAppPrefs<DiscoveriesExpandedPrefs>(DISCOVERIES_EXPANDED_KEY)
  return prefs?.[tenantId] ?? false
}

// ============================================================================
// OVERVIEW DASHBOARD PREFERENCES
// ============================================================================

// Overview dashboard view preference helper functions
export const setOverviewDashboardView = (
  dashboardView: 'executive' | 'analyst',
) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save overview dashboard view preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<OverviewDashboardPrefs>(
    OVERVIEW_DASHBOARD_PREFS_KEY,
  ) || {
    dashboardView: 'executive',
    compactView: false,
  }
  const updatedPrefs: OverviewDashboardPrefs = {
    ...existingPrefs,
    dashboardView,
  }
  setAppPrefs(updatedPrefs, OVERVIEW_DASHBOARD_PREFS_KEY)
}

export const getOverviewDashboardView = (): 'executive' | 'analyst' => {
  const prefs = getAppPrefs<OverviewDashboardPrefs>(
    OVERVIEW_DASHBOARD_PREFS_KEY,
  )
  return prefs?.dashboardView ?? 'executive' // Default to executive view
}

// Overview dashboard compact view preference helper functions
export const setOverviewCompactView = (compactView: boolean) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save overview compact view preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<OverviewDashboardPrefs>(
    OVERVIEW_DASHBOARD_PREFS_KEY,
  ) || {
    dashboardView: 'executive',
    compactView: false,
  }
  const updatedPrefs: OverviewDashboardPrefs = {
    ...existingPrefs,
    compactView,
  }
  setAppPrefs(updatedPrefs, OVERVIEW_DASHBOARD_PREFS_KEY)
}

export const getOverviewCompactView = (): boolean => {
  const prefs = getAppPrefs<OverviewDashboardPrefs>(
    OVERVIEW_DASHBOARD_PREFS_KEY,
  )
  return prefs?.compactView ?? false // Default to full view
}

// Combined helper to get all overview dashboard preferences
export const getOverviewDashboardPrefs = (): OverviewDashboardPrefs => {
  const prefs = getAppPrefs<OverviewDashboardPrefs>(
    OVERVIEW_DASHBOARD_PREFS_KEY,
  )
  return (
    prefs || {
      dashboardView: 'executive',
      compactView: false,
    }
  )
}

// ============================================================================
// AG GRID PAGE SIZE PREFERENCES
// ============================================================================

export const getPageSizePrefsLocalStorageKey = (id?: string): string => {
  return `datagrid_pagesize_prefs_${id || 'generic_table'}`
}

// Loads the saved page size preference from LocalStorage for the specified grid (keyed off of the id)
export const loadPageSizeState = (id?: string): number | undefined => {
  const storageKey = getPageSizePrefsLocalStorageKey(id)
  const savedState = localStorage.getItem(storageKey)
  if (!savedState) {
    return undefined
  }
  try {
    const pageSize = parseInt(savedState, 10)
    // Validate that the page size is a reasonable number
    return isNaN(pageSize) || pageSize <= 0 ? undefined : pageSize
  } catch (e) {
    console.error(`Error parsing saved page size prefs for ${storageKey}`, e)
    return undefined
  }
}

// Saves the page size preference to LocalStorage for the specified grid (keyed off of the id)
export const savePageSizeState = (pageSize: number, id?: string) => {
  if (pageSize > 0) {
    localStorage.setItem(
      getPageSizePrefsLocalStorageKey(id),
      pageSize.toString(),
    )
  }
}

export const removePageSizeState = (id?: string) => {
  localStorage.removeItem(getPageSizePrefsLocalStorageKey(id))
}

// ============================================================================
// SECTION CARD COLLAPSE PREFERENCES
// ============================================================================

// Section card collapse helper functions
export const setSectionCardCollapse = (
  sectionId: string,
  isExpanded: boolean,
) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save section card collapse preferences - localStorage not available',
    )
    return
  }

  const existingPrefs =
    getAppPrefs<SectionCardCollapsePrefs>(SECTION_CARD_PREFS_KEY) || {}
  const updatedPrefs: SectionCardCollapsePrefs = {
    ...existingPrefs,
    [sectionId]: isExpanded,
  }
  setAppPrefs(updatedPrefs, SECTION_CARD_PREFS_KEY)
}

export const getSectionCardCollapse = (
  sectionId: string,
  defaultExpanded: boolean = true,
): boolean => {
  const prefs = getAppPrefs<SectionCardCollapsePrefs>(SECTION_CARD_PREFS_KEY)
  if (!prefs || !(sectionId in prefs)) {
    return defaultExpanded
  }
  return prefs[sectionId]
}

// Helper to get all section card preferences
export const getSectionCardCollapsePrefs = (): SectionCardCollapsePrefs => {
  return getAppPrefs<SectionCardCollapsePrefs>(SECTION_CARD_PREFS_KEY) || {}
}

// Helper to clear section card preferences (useful for debugging/reset)
export const clearSectionCardCollapsePrefs = () => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot clear section card collapse preferences - localStorage not available',
    )
    return
  }
  localStorage.removeItem(SECTION_CARD_PREFS_KEY)
}

// ============================================================================
// ELK LAYOUT PREFERENCES
// ============================================================================

export const ELK_LAYOUT_PREFS_KEY = 'elk_layout_prefs'

export type ELKLayoutPrefs = {
  activePresetId?: string // Currently selected preset ID (e.g., '1', '2', '12')
  customOptions?: Record<string, string> // Custom ELK options when not using a preset
}

// ELK layout preference helper functions
export const setELKLayoutPrefs = (
  activePresetId?: string,
  customOptions?: Record<string, string>,
) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save ELK layout preferences - localStorage not available',
    )
    return
  }

  const prefs: ELKLayoutPrefs = {
    ...(activePresetId && { activePresetId }),
    ...(customOptions && { customOptions }),
  }
  setAppPrefs(prefs, ELK_LAYOUT_PREFS_KEY)
}

export const getELKLayoutPrefs = (): ELKLayoutPrefs => {
  const prefs = getAppPrefs<ELKLayoutPrefs>(ELK_LAYOUT_PREFS_KEY)
  return (
    prefs || {
      activePresetId: '1', // Default to first preset
    }
  )
}

export const getELKActivePresetId = (): string => {
  const prefs = getELKLayoutPrefs()
  return prefs.activePresetId || '1' // Default to first preset
}

export const setELKActivePresetId = (presetId: string) => {
  // Clear customOptions when switching to a preset - we want the preset, not custom options
  setELKLayoutPrefs(presetId, undefined)
}

export const setELKCustomOptions = (customOptions: Record<string, string>) => {
  setELKLayoutPrefs(undefined, customOptions) // Clear preset ID when using custom options
}

// ============================================================================
// BLAST RADIUS GRAPH PREFERENCES
// ============================================================================

export const BLAST_RADIUS_GRAPH_PREFS_KEY = 'blast_radius_graph_prefs'
/** Second bucket for the Workflow-preset surfaces (id '3'). Split from
 *  BLAST_RADIUS_GRAPH_PREFS_KEY so a user's workflow-view choices
 *  (colorMode = ai-role, edgeRouting = ORTHOGONAL, etc.) don't
 *  cross-contaminate the blast-radius drawer — the two surfaces have
 *  different palette/routing defaults and independent user memory. */
export const WORKFLOW_GRAPH_PREFS_KEY = 'workflow_graph_prefs'

/** Which visualization surface's prefs to read/write. `blast-radius`
 *  (default) covers the classic drawer + Ripple + AI Workload tabs.
 *  `workflow` covers callers that mount with `defaultPresetId="3"`
 *  (App Blast, GraphTest workflow tabs, identity cascade). */
export type BlastRadiusPrefsSurface = 'blast-radius' | 'workflow'

const prefsKeyFor = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): string =>
  surface === 'workflow'
    ? WORKFLOW_GRAPH_PREFS_KEY
    : BLAST_RADIUS_GRAPH_PREFS_KEY

export type ReachMode = 'observed' | 'permitted'

export type BlastRadiusGraphPrefs = {
  showEdgeLabels?: boolean // Whether to show edge labels in the graph
  colorMode?: 'type' | 'risk' | 'severity' | 'ai-role' // Node and edge coloring mode
  groupingStrategy?: 'none' | 'siblings' | 'region' | 'type' // Node grouping strategy
  direction?: 'LEFT' | 'RIGHT' | 'UP' | 'DOWN' // Graph direction/layout orientation
  hideLowRisk?: boolean // Whether to hide nodes with risk < 60
  showSwimlanes?: boolean // Whether to enforce lane partitioning + draw lane backgrounds
  edgeRouting?: 'STEP' | 'ANGULAR' | 'SMOOTH' // Edge rendering style
  reachMode?: ReachMode // Observed (default) vs permitted reach
  showDelta?: boolean // Whether the "added reach" delta overlay is visible
  showLegend?: boolean // Whether the graph legend sidebar is visible
}

// Blast radius graph preference helper functions.
// Every helper accepts a `surface` (default 'blast-radius') that picks
// the localStorage bucket. Callers on Workflow-preset surfaces pass
// 'workflow' so their reads/writes don't touch the blast-radius bucket
// (and vice versa). See `BlastRadiusPrefsSurface` above.
export const setBlastRadiusGraphPrefs = (
  prefs: BlastRadiusGraphPrefs,
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save blast radius graph preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getBlastRadiusGraphPrefs(surface)
  const updatedPrefs: BlastRadiusGraphPrefs = {
    ...existingPrefs,
    ...prefs,
  }
  setAppPrefs(updatedPrefs, prefsKeyFor(surface))
}

export const getBlastRadiusGraphPrefs = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): BlastRadiusGraphPrefs => {
  const prefs = getAppPrefs<BlastRadiusGraphPrefs>(prefsKeyFor(surface))
  return (
    prefs || {
      showEdgeLabels: false, // Default to false
      // `colorMode` intentionally omitted — the per-field getters
      // (e.g. `getBlastRadiusColorMode(fallback)`) handle the
      // fallback, and callers may pass a preset-aware default that
      // differs from the schema-wide default. Including a value here
      // pre-empts the caller's fallback for fresh-localStorage users
      // (see BlastRadiusVisualization's preset-inferred default —
      // Workflow preset pairs with `ai-role`, not `risk`).
      groupingStrategy: 'siblings', // Default to siblings grouping
      direction: 'RIGHT', // Default to right-to-left (dirRightToLeft)
    }
  )
}

export const getBlastRadiusShowEdgeLabels = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): boolean => {
  const prefs = getBlastRadiusGraphPrefs(surface)
  return prefs.showEdgeLabels ?? false // Default to false
}

export const setBlastRadiusShowEdgeLabels = (
  showEdgeLabels: boolean,
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  setBlastRadiusGraphPrefs({ showEdgeLabels }, surface)
}

export const getBlastRadiusColorMode = (
  // Per-caller fallback when the user hasn't persisted a colorMode
  // yet. `BlastRadiusVisualization` passes 'ai-role' for the Workflow
  // preset (id '3') and 'risk' for everything else — the schema-wide
  // default here is 'risk' since that's the blast-radius classic
  // baseline.
  fallback: 'type' | 'risk' | 'severity' | 'ai-role' = 'risk',
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): 'type' | 'risk' | 'severity' | 'ai-role' => {
  const prefs = getBlastRadiusGraphPrefs(surface)
  return prefs.colorMode ?? fallback
}

export const setBlastRadiusColorMode = (
  colorMode: 'type' | 'risk' | 'severity' | 'ai-role',
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  setBlastRadiusGraphPrefs({ colorMode }, surface)
}

export const getBlastRadiusReachMode = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): ReachMode => {
  const prefs = getBlastRadiusGraphPrefs(surface)
  return prefs.reachMode ?? 'observed' // Default per the P2d PRD
}

export const setBlastRadiusReachMode = (
  reachMode: ReachMode,
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  setBlastRadiusGraphPrefs({ reachMode }, surface)
}

export const getBlastRadiusShowDelta = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): boolean => {
  const prefs = getBlastRadiusGraphPrefs(surface)
  return prefs.showDelta ?? false // Default off — delta is hidden until opt-in
}

export const setBlastRadiusShowDelta = (
  showDelta: boolean,
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  setBlastRadiusGraphPrefs({ showDelta }, surface)
}

export const getBlastRadiusShowLegend = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): boolean => {
  const prefs = getBlastRadiusGraphPrefs(surface)
  return prefs.showLegend ?? false // Default off — user toggles it via the toolbar
}

export const setBlastRadiusShowLegend = (
  showLegend: boolean,
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  setBlastRadiusGraphPrefs({ showLegend }, surface)
}

export const getBlastRadiusGroupingStrategy = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): 'none' | 'siblings' | 'region' | 'type' => {
  const prefs = getBlastRadiusGraphPrefs(surface)
  return prefs.groupingStrategy ?? 'siblings' // Default to siblings grouping
}

export const setBlastRadiusGroupingStrategy = (
  groupingStrategy: 'none' | 'siblings' | 'region' | 'type',
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  setBlastRadiusGraphPrefs({ groupingStrategy }, surface)
}

export const getBlastRadiusDirection = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): 'LEFT' | 'RIGHT' | 'UP' | 'DOWN' => {
  const prefs = getBlastRadiusGraphPrefs(surface)
  return prefs.direction ?? 'RIGHT' // Default to right-to-left
}

export const setBlastRadiusDirection = (
  direction: 'LEFT' | 'RIGHT' | 'UP' | 'DOWN',
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  setBlastRadiusGraphPrefs({ direction }, surface)
}

export const getBlastRadiusHideLowRisk = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): boolean => {
  const prefs = getBlastRadiusGraphPrefs(surface)
  return prefs.hideLowRisk ?? false // Default to showing all nodes
}

export const setBlastRadiusHideLowRisk = (
  hideLowRisk: boolean,
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  setBlastRadiusGraphPrefs({ hideLowRisk }, surface)
}

export const getBlastRadiusShowSwimlanes = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): boolean => {
  const prefs = getBlastRadiusGraphPrefs(surface)
  return prefs.showSwimlanes ?? true // Default on — only meaningful for lane-tagged graphs
}

export const setBlastRadiusShowSwimlanes = (
  showSwimlanes: boolean,
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  setBlastRadiusGraphPrefs({ showSwimlanes }, surface)
}

export const getBlastRadiusEdgeRouting = (
  surface: BlastRadiusPrefsSurface = 'blast-radius',
): 'STEP' | 'ANGULAR' | 'SMOOTH' => {
  const prefs = getBlastRadiusGraphPrefs(surface)
  // Migrate prior labels:
  //   ORTHOGONAL → STEP   (renamed; same semantics — ELK step routing)
  //   SPLINES    → SMOOTH (renamed; same semantics — bezier between handles)
  const raw = prefs.edgeRouting as unknown as string | undefined
  if (raw === 'ORTHOGONAL') return 'STEP'
  if (raw === 'SPLINES') return 'SMOOTH'
  return prefs.edgeRouting ?? 'STEP'
}

export const setBlastRadiusEdgeRouting = (
  edgeRouting: 'STEP' | 'ANGULAR' | 'SMOOTH',
  surface: BlastRadiusPrefsSurface = 'blast-radius',
) => {
  setBlastRadiusGraphPrefs({ edgeRouting }, surface)
}

// ============================================================================
// ATTACK PATH VIEW MODE PREFERENCES
// ============================================================================

// Attack path view mode preference helper functions
export const setAttackPathViewMode = (viewMode: 'timeline' | 'grouped') => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save attack path view mode preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAppPrefs<DashboardViewPrefs>(
    DASHBOARD_PREFS_KEYS.INCIDENTS,
  ) || { viewMode: 'grid' }
  const updatedPrefs: DashboardViewPrefs = {
    ...existingPrefs,
    attackPathViewMode: viewMode,
  }
  setAppPrefs(updatedPrefs, DASHBOARD_PREFS_KEYS.INCIDENTS)
}

export const getAttackPathViewMode = (): 'timeline' | 'grouped' => {
  const prefs = getAppPrefs<DashboardViewPrefs>(DASHBOARD_PREFS_KEYS.INCIDENTS)
  return prefs?.attackPathViewMode || 'timeline' // Default to timeline view
}

// ============================================================================
// ATTACK PATH GRAPH PREFERENCES
// ============================================================================

export const ATTACK_PATH_GRAPH_PREFS_KEY = 'attack_path_graph_prefs'

export type AttackPathGraphPrefs = {
  showEdgeLabels?: boolean // Whether to show edge labels in the graph
  colorMode?: 'severity' | 'tactic' | 'type' // Node and edge coloring mode
  groupingMode?: 'timeline' | 'tactic' | 'resource' // Grouping mode for events
  direction?: 'LEFT' | 'RIGHT' | 'UP' | 'DOWN' // Graph direction/layout orientation
}

// Attack path graph preference helper functions
export const setAttackPathGraphPrefs = (prefs: AttackPathGraphPrefs) => {
  if (!canSetAppPrefs()) {
    console.warn(
      'Cannot save attack path graph preferences - localStorage not available',
    )
    return
  }

  const existingPrefs = getAttackPathGraphPrefs()
  const updatedPrefs: AttackPathGraphPrefs = {
    ...existingPrefs,
    ...prefs,
  }
  setAppPrefs(updatedPrefs, ATTACK_PATH_GRAPH_PREFS_KEY)
}

export const getAttackPathGraphPrefs = (): AttackPathGraphPrefs => {
  const prefs = getAppPrefs<AttackPathGraphPrefs>(ATTACK_PATH_GRAPH_PREFS_KEY)
  return (
    prefs || {
      showEdgeLabels: true, // Default to true for attack paths (show time deltas)
      colorMode: 'severity', // Default to severity-based coloring
      groupingMode: 'timeline', // Default to timeline (ungrouped)
      direction: 'LEFT', // Default to left-to-right (time progression)
    }
  )
}

export const getAttackPathShowEdgeLabels = (): boolean => {
  const prefs = getAttackPathGraphPrefs()
  return prefs.showEdgeLabels ?? true // Default to true
}

export const setAttackPathShowEdgeLabels = (showEdgeLabels: boolean) => {
  setAttackPathGraphPrefs({ showEdgeLabels })
}

export const getAttackPathColorMode = (): 'severity' | 'tactic' | 'type' => {
  const prefs = getAttackPathGraphPrefs()
  return prefs.colorMode ?? 'severity' // Default to severity-based coloring
}

export const setAttackPathColorMode = (
  colorMode: 'severity' | 'tactic' | 'type',
) => {
  setAttackPathGraphPrefs({ colorMode })
}

export const getAttackPathGroupingMode = ():
  'timeline' | 'tactic' | 'resource' => {
  const prefs = getAttackPathGraphPrefs()
  return prefs.groupingMode ?? 'timeline' // Default to timeline (ungrouped)
}

export const setAttackPathGroupingMode = (
  groupingMode: 'timeline' | 'tactic' | 'resource',
) => {
  setAttackPathGraphPrefs({ groupingMode })
}

export const getAttackPathDirection = (): 'LEFT' | 'RIGHT' | 'UP' | 'DOWN' => {
  const prefs = getAttackPathGraphPrefs()
  return prefs.direction ?? 'LEFT' // Default to left-to-right
}

export const setAttackPathDirection = (
  direction: 'LEFT' | 'RIGHT' | 'UP' | 'DOWN',
) => {
  setAttackPathGraphPrefs({ direction })
}

// ─── Generic drawer tab helpers ───────────────────────────────────────────────
// Use these with DASHBOARD_PREFS_KEYS.APP_DRAWER_* constants instead of writing
// per-drawer boilerplate.

/**
 * Persist the active tab index for any drawer identified by `prefsKey`.
 */
export const setDrawerActiveTab = (
  prefsKey: string,
  tabIndex: number,
): void => {
  if (!canSetAppPrefs()) return
  const existing = getAppPrefs<DashboardViewPrefs>(prefsKey) || {
    viewMode: 'grid',
  }
  setAppPrefs({ ...existing, activeTab: tabIndex.toString() }, prefsKey)
}

/**
 * Retrieve the persisted tab index for a drawer, clamped to [0, maxIndex].
 */
export const getDrawerActiveTab = (
  prefsKey: string,
  maxIndex: number,
): number => {
  const prefs = getAppPrefs<DashboardViewPrefs>(prefsKey)
  const parsed = parseInt(prefs?.activeTab ?? '0', 10)
  return isNaN(parsed) || parsed < 0 || parsed > maxIndex ? 0 : parsed
}

// AI Identities drawer uses string tab ids ('overview', 'security-issues',
// 'tags', 'access-map') rather than numeric indices because the visible-tab
// list is feature-flag-gated and order isn't stable across flag states.
const AI_IDENTITY_DRAWER_PREFS_KEY = 'ai_identity_drawer_tab'

export const setAIIdentitiesActiveTab = (tab: string): void => {
  if (!canSetAppPrefs()) return
  const existing = getAppPrefs<DashboardViewPrefs>(
    AI_IDENTITY_DRAWER_PREFS_KEY,
  ) || { viewMode: 'grid' }
  setAppPrefs({ ...existing, activeTab: tab }, AI_IDENTITY_DRAWER_PREFS_KEY)
}

export const getAIIdentitiesActiveTab = (): string | null => {
  const prefs = getAppPrefs<DashboardViewPrefs>(AI_IDENTITY_DRAWER_PREFS_KEY)
  return prefs?.activeTab ?? null
}

import {
  type ApiNotificationSetting,
  type ApiNotificationSettingUpdate,
  resetNotificationSettings as apiResetNotificationSettings,
  saveNotificationSettings as apiSaveNotificationSettings,
} from '../services/api/aiNotificationSettings'

/** One of the three defined trigger types (PRD §Glossary + NG-3). */
export type NotificationCategoryId =
  'ai-security-concerns' | 'ai-identity-risk' | 'shadow-ai-discovery'

/** Severity threshold options for trigger types that support tiering.
 *  `'high-critical'` matches the platform default (PRD §Glossary → Platform
 *  default); `'critical-only'` is the narrower user-selectable option. */
export type NotificationThreshold = 'high-critical' | 'critical-only'

export type NotificationCategorySetting = {
  enabled: boolean
  /** Only meaningful for categories with defined severity tiers. Ignored
   *  for categories that support enable/disable only (Shadow AI today —
   *  PRD NG-5 / FR-6.1.3). */
  threshold?: NotificationThreshold
}

/** Platform default per PRD §Glossary + Risk R-1: all three trigger types
 *  enabled; the two thresholded types default to their broader tier
 *  (High & Critical). Users only ever land here on first visit or via
 *  "Reset to defaults" (FR-6.1.5). */
export const NOTIFICATION_SETTINGS_DEFAULTS: Record<
  NotificationCategoryId,
  NotificationCategorySetting
> = {
  'ai-security-concerns': { enabled: true, threshold: 'high-critical' },
  'ai-identity-risk': { enabled: true, threshold: 'high-critical' },
  'shadow-ai-discovery': { enabled: true },
}

/** Canonical wire order for the three categories — the shape
 *  `setNotificationSettings` sends and the projection reads back. */
const NOTIFICATION_CATEGORY_ORDER: readonly NotificationCategoryId[] = [
  'ai-security-concerns',
  'ai-identity-risk',
  'shadow-ai-discovery',
]

/** Fresh deep copy of the platform defaults — never the shared
 *  `NOTIFICATION_SETTINGS_DEFAULTS` constant — so a caller mutating the
 *  result can't corrupt the global default. */
const cloneNotificationDefaults = (): Record<
  NotificationCategoryId,
  NotificationCategorySetting
> =>
  NOTIFICATION_CATEGORY_ORDER.reduce(
    (record, category) => {
      record[category] = { ...NOTIFICATION_SETTINGS_DEFAULTS[category] }
      return record
    },
    {} as Record<NotificationCategoryId, NotificationCategorySetting>,
  )

/** Projects the API's `settings[]` wire rows onto the record the tab uses,
 *  starting from the platform defaults so every category is always present.
 *  A null `severity_threshold` yields a setting with no `threshold` key. */
export const projectNotificationSettings = (
  settings: ApiNotificationSetting[] | undefined,
): Record<NotificationCategoryId, NotificationCategorySetting> => {
  const record = cloneNotificationDefaults()
  for (const row of settings ?? []) {
    record[row.category] =
      row.severity_threshold == null
        ? { enabled: row.enabled }
        : { enabled: row.enabled, threshold: row.severity_threshold }
  }
  return record
}

/** Persist the full per-category snapshot for this tenant (PUT replaces all
 *  rows) and return the projected, persisted state. Null / empty tenant is a
 *  no-op that resolves to a fresh copy of the platform defaults. */
export const setNotificationSettings = async (
  tenantId: string | null | undefined,
  settings: Record<NotificationCategoryId, NotificationCategorySetting>,
): Promise<Record<NotificationCategoryId, NotificationCategorySetting>> => {
  if (!tenantId) return cloneNotificationDefaults()
  const rows: ApiNotificationSettingUpdate[] = NOTIFICATION_CATEGORY_ORDER.map(
    category => ({
      category,
      enabled: settings[category].enabled,
      severity_threshold: settings[category].threshold ?? null,
    }),
  )
  const res = await apiSaveNotificationSettings(tenantId, rows)
  return projectNotificationSettings(res?.settings)
}

/** Reset one tenant's settings to the platform defaults via the API and
 *  return the projected, persisted state. Null / empty tenant resolves to a
 *  fresh copy of the defaults with no network call. */
export const clearNotificationSettings = async (
  tenantId: string | null | undefined,
): Promise<Record<NotificationCategoryId, NotificationCategorySetting>> => {
  if (!tenantId) return cloneNotificationDefaults()
  const res = await apiResetNotificationSettings(tenantId)
  return projectNotificationSettings(res?.settings)
}
